/**
 * Affiliate Manager page — per docs/specs/affiliate-manager.md
 *
 * Move the route from scaffold to live, server-backed views:
 * - KPI rollup
 * - Applications review queue
 * - Partner roster
 * - Contract status for missing affiliate endpoints
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-22-v1
 */
import { CheckCircle2, Download, Eye, Megaphone, ShieldAlert, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Chip } from '../components/chip';
import { KpiTile } from '../components/kpi-tile';
import { Modal } from '../components/modal';
import { RoleButton } from '../components/role-gate';
import { useToast } from '../components/toast';
import {
    apiDownloadText,
    apiGet,
    apiPatch,
    apiPost,
    getHttpErrorMessage,
    HttpError
} from '../../lib/http';
import type { Kpi } from '../../lib/types';

type EndpointStatus = 'loading' | 'ready' | 'empty' | 'missing' | 'error';

interface AffiliateKpis {
    active_affiliates: number;
    applications_pending: number;
    attributed_trials_30d: number;
    paid_conversions_30d: number;
    commission_due: number;
    fraud_queue: number;
}

interface AffiliateApplication {
    id: string;
    applicant_name: string;
    applicant_email: string;
    channels: string[];
    audience_size: number;
    geo: string;
    submitted_at: string;
    risk_hints: string[];
}

type AffiliateStatus = 'pending' | 'active' | 'paused' | 'blocked';

interface AffiliatePartner {
    id: string;
    name: string;
    handle: string;
    status: AffiliateStatus;
    primary_code: string;
    clicks: number;
    signups: number;
    paid_conversions: number;
    conversion_rate: number;
    lifetime_revenue: number;
    commission_paid: number;
    commission_due: number;
}

interface AffiliateAttributionChainRow {
    id: string;
    event_type: string;
    amount: number;
    currency: string;
    occurred_at: string;
    detail: string;
}

interface AffiliateAttributionRow {
    id: string;
    affiliate_id: string;
    event_type: string;
    user_email: string | null;
    user_name: string | null;
    link_code: string | null;
    amount: number;
    currency: string;
    occurred_at: string;
    detail: string;
    chain: AffiliateAttributionChainRow[];
}

interface AffiliateCommissionRow {
    id: string;
    period: string;
    partner: string;
    handle: string;
    attributed_revenue: number;
    commission_rate: number;
    commission_amount: number;
    holdback: number;
    adjustments: number;
    final_payable: number;
    status: string;
    due_at: string | null;
    paid_at: string | null;
    event_chain: AffiliateAttributionChainRow[];
}

interface AffiliatePayoutRow {
    id: string;
    partner: string;
    handle: string;
    status: string;
    provider: string;
    provider_reference: string | null;
    held_reason: string | null;
    amount: number;
    currency: string;
    paid_at: string | null;
    created_at: string | null;
    updated_at: string | null;
    provider_unavailable: boolean;
}

interface AffiliateFraudRow {
    id: string;
    partner: string;
    handle: string;
    severity: 'low' | 'medium' | 'high';
    status: string;
    reason: string;
    payout_status: string | null;
    created_at: string | null;
    resolved_at: string | null;
}

interface AffiliatePartnerDetail {
    affiliate: AffiliatePartner & {
        commission_rate: number;
        notes: string | null;
        active_links: number;
        provider_unavailable: boolean;
    };
    links: Array<{
        id: string;
        code: string;
        destination_url: string;
        is_active: boolean;
        created_at: string;
    }>;
    attribution: AffiliateAttributionRow[];
    commissions: AffiliateCommissionRow[];
    payouts: AffiliatePayoutRow[];
    fraud: AffiliateFraudRow[];
    event_chains: Array<{
        id: string;
        user_email: string | null;
        amount: number;
        occurred_at: string;
        chain: AffiliateAttributionChainRow[];
    }>;
}

interface PendingReviewAction {
    applicationId: string;
    applicantLabel: string;
    action: 'approve' | 'reject';
}

type AffiliatePeriod = '30d' | '90d' | 'ytd';

const FLAT_SPARK = new Array(12).fill(0);

const CONTRACT_ROUTES = [
    {
        method: 'GET',
        endpoint: '/api/admin/affiliates/kpis',
        purpose: 'Program KPI rollup for this page'
    },
    {
        method: 'GET',
        endpoint: '/api/admin/affiliates/applications',
        purpose: 'Application queue (review queue + actions)'
    },
    {
        method: 'POST',
        endpoint: '/api/admin/affiliates/applications/:id/approve',
        purpose: 'Approve applicant into active partner flow'
    },
    {
        method: 'POST',
        endpoint: '/api/admin/affiliates/applications/:id/reject',
        purpose: 'Reject applicant with mandatory reason'
    },
    {
        method: 'GET',
        endpoint: '/api/admin/affiliates/partners',
        purpose: 'Partner roster + performance tile'
    },
    {
        method: 'GET',
        endpoint: '/api/admin/affiliates/partners/:id',
        purpose: 'Partner detail + attribution chain'
    },
    {
        method: 'PATCH',
        endpoint: '/api/admin/affiliates/partners/:id',
        purpose: 'Update partner status / commission / notes'
    },
    {
        method: 'POST',
        endpoint: '/api/admin/affiliates/partners/:id/links',
        purpose: 'Create or rotate tracked code/link'
    },
    {
        method: 'GET',
        endpoint: '/api/admin/affiliates/attribution',
        purpose: 'Attribution inspector rows'
    },
    {
        method: 'GET',
        endpoint: '/api/admin/affiliates/commissions',
        purpose: 'Commission ledger'
    },
    {
        method: 'GET',
        endpoint: '/api/admin/affiliates/payouts',
        purpose: 'Payout queue + history'
    },
    {
        method: 'POST',
        endpoint: '/api/admin/affiliates/payouts/:id/hold',
        purpose: 'Hold payout with audit reason'
    },
    {
        method: 'POST',
        endpoint: '/api/admin/affiliates/payouts/:id/release',
        purpose: 'Release payout to manual or provider-backed flow'
    },
    {
        method: 'GET',
        endpoint: '/api/admin/affiliates/fraud',
        purpose: 'Fraud review queue'
    },
    {
        method: 'POST',
        endpoint: '/api/admin/affiliates/fraud/:id/resolve',
        purpose: 'Resolve / suppress / block payout'
    },
    {
        method: 'GET',
        endpoint: '/api/admin/affiliates/export.csv',
        purpose: 'Monthly statement export'
    }
];

function isContractMissing(error: unknown): boolean {
    return error instanceof HttpError && error.status === 404;
}

function getEndpointStatus(isLoading: boolean, error: unknown, hasData: boolean): EndpointStatus {
    if (isLoading) return 'loading';
    if (error) return isContractMissing(error) ? 'missing' : 'error';
    if (!hasData) return 'empty';
    return 'ready';
}

function statusTone(status: AffiliateStatus): 'success' | 'warning' | 'danger' | 'muted' | 'info' {
    switch (status) {
        case 'active':
            return 'success';
        case 'pending':
            return 'warning';
        case 'paused':
            return 'info';
        case 'blocked':
            return 'danger';
        default:
            return 'muted';
    }
}

function formatMoney(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) return '—';
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(value: string | null | undefined): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('en-AU', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

function useAffiliateKpis() {
    return useQuery<AffiliateKpis>({
        queryKey: ['admin', 'affiliate-manager', 'kpis'],
        queryFn: () => apiGet<AffiliateKpis>('/api/admin/affiliates/kpis'),
        refetchInterval: 30_000,
        retry: false
    });
}

function useAffiliateApplications() {
    return useQuery<AffiliateApplication[]>({
        queryKey: ['admin', 'affiliate-manager', 'applications'],
        queryFn: () => apiGet<AffiliateApplication[]>('/api/admin/affiliates/applications'),
        refetchInterval: 30_000,
        retry: false
    });
}

function useAffiliatePartners() {
    return useQuery<AffiliatePartner[]>({
        queryKey: ['admin', 'affiliate-manager', 'partners'],
        queryFn: () => apiGet<AffiliatePartner[]>('/api/admin/affiliates/partners'),
        refetchInterval: 30_000,
        retry: false
    });
}

function useAffiliatePartnerDetail(affiliateId: string | null) {
    return useQuery<AffiliatePartnerDetail>({
        queryKey: ['admin', 'affiliate-manager', 'partner', affiliateId],
        queryFn: () =>
            apiGet<AffiliatePartnerDetail>(`/api/admin/affiliates/partners/${affiliateId}`),
        enabled: !!affiliateId,
        retry: false
    });
}

function useAffiliateAttribution(period: AffiliatePeriod, affiliateId: string | null = null) {
    const searchParams = new URLSearchParams({ period });
    if (affiliateId) searchParams.set('affiliate_id', affiliateId);

    return useQuery<AffiliateAttributionRow[]>({
        queryKey: ['admin', 'affiliate-manager', 'attribution', period, affiliateId],
        queryFn: () =>
            apiGet<AffiliateAttributionRow[]>(`/api/admin/affiliates/attribution?${searchParams}`),
        refetchInterval: 60_000,
        retry: false
    });
}

function useAffiliateCommissions(period: AffiliatePeriod, affiliateId: string | null = null) {
    const searchParams = new URLSearchParams({ period });
    if (affiliateId) searchParams.set('affiliate_id', affiliateId);

    return useQuery<AffiliateCommissionRow[]>({
        queryKey: ['admin', 'affiliate-manager', 'commissions', period, affiliateId],
        queryFn: () =>
            apiGet<AffiliateCommissionRow[]>(`/api/admin/affiliates/commissions?${searchParams}`),
        refetchInterval: 60_000,
        retry: false
    });
}

function useAffiliatePayouts(affiliateId: string | null = null) {
    const searchParams = new URLSearchParams();
    if (affiliateId) searchParams.set('affiliate_id', affiliateId);

    return useQuery<{
        rows: AffiliatePayoutRow[];
        summary: { total: number; provider_unavailable: number; by_status: Record<string, number> };
        provider_available: boolean;
    }>({
        queryKey: ['admin', 'affiliate-manager', 'payouts', affiliateId],
        queryFn: () =>
            apiGet<{
                rows: AffiliatePayoutRow[];
                summary: {
                    total: number;
                    provider_unavailable: number;
                    by_status: Record<string, number>;
                };
                provider_available: boolean;
            }>(`/api/admin/affiliates/payouts${searchParams.toString() ? `?${searchParams}` : ''}`),
        refetchInterval: 60_000,
        retry: false
    });
}

function useAffiliateFraud(affiliateId: string | null = null) {
    const searchParams = new URLSearchParams();
    if (affiliateId) searchParams.set('affiliate_id', affiliateId);

    return useQuery<AffiliateFraudRow[]>({
        queryKey: ['admin', 'affiliate-manager', 'fraud', affiliateId],
        queryFn: () =>
            apiGet<AffiliateFraudRow[]>(
                `/api/admin/affiliates/fraud${searchParams.toString() ? `?${searchParams}` : ''}`
            ),
        refetchInterval: 60_000,
        retry: false
    });
}

function getContractChipTone(status: EndpointStatus) {
    switch (status) {
        case 'ready':
            return 'success';
        case 'empty':
            return 'warning';
        case 'loading':
            return 'muted';
        case 'missing':
            return 'danger';
        case 'error':
            return 'warning';
        default:
            return 'muted';
    }
}

function endpointStatusLabel(status: EndpointStatus): string {
    switch (status) {
        case 'ready':
            return 'Live';
        case 'empty':
            return 'No rows';
        case 'loading':
            return 'Loading';
        case 'missing':
            return 'Missing route';
        case 'error':
            return 'Error';
    }
}

function formatRate(value: number | null | undefined): string {
    if (typeof value !== 'number' || Number.isNaN(value)) return '—';
    return `${value.toFixed(1)}%`;
}

function formatInteger(value: number | null | undefined): string {
    if (typeof value !== 'number' || Number.isNaN(value)) return '—';
    return value.toLocaleString();
}

function formatDecimal(value: number | null | undefined): string {
    if (typeof value !== 'number' || Number.isNaN(value)) return '—';
    return value.toFixed(2);
}

function formatShortDate(value: string | null | undefined): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-AU', {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
    });
}

function formatShortDateTime(value: string | null | undefined): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('en-AU', {
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
}

function toPeriodLabel(period: AffiliatePeriod): string {
    if (period === '90d') return 'Last 90 days';
    if (period === 'ytd') return 'Year to date';
    return 'Last 30 days';
}

function buildStatementExportUrl(period: AffiliatePeriod) {
    const params = new URLSearchParams({
        period,
        reason: 'monthly statement export'
    });
    return `/api/admin/affiliates/export.csv?${params.toString()}`;
}

function actionToPayoutEndpoint(action: 'hold' | 'release', payoutId: string) {
    return `/api/admin/affiliates/payouts/${payoutId}/${action}`;
}

function actionToFraudEndpoint(flagId: string) {
    return `/api/admin/affiliates/fraud/${flagId}/resolve`;
}

export function AffiliateManagerPage() {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const kpis = useAffiliateKpis();
    const applications = useAffiliateApplications();
    const partners = useAffiliatePartners();
    const [period, setPeriod] = useState<AffiliatePeriod>('30d');
    const [detailPartnerId, setDetailPartnerId] = useState<string | null>(null);
    const [exportBusy, setExportBusy] = useState(false);

    const [reviewAction, setReviewAction] = useState<PendingReviewAction | null>(null);
    const [reviewReason, setReviewReason] = useState('');
    const [reviewBusy, setReviewBusy] = useState(false);
    const partnerDetail = useAffiliatePartnerDetail(detailPartnerId);
    const attribution = useAffiliateAttribution(period);
    const commissions = useAffiliateCommissions(period);
    const payouts = useAffiliatePayouts();
    const fraud = useAffiliateFraud();

    const contractStatus = {
        kpis: getEndpointStatus(kpis.isLoading, kpis.error, !!kpis.data),
        applications: getEndpointStatus(
            applications.isLoading,
            applications.error,
            !!(applications.data && applications.data.length > 0)
        ),
        partners: getEndpointStatus(
            partners.isLoading,
            partners.error,
            !!(partners.data && partners.data.length > 0)
        ),
        detail: getEndpointStatus(
            partnerDetail.isLoading,
            partnerDetail.error,
            !!partnerDetail.data
        ),
        attribution: getEndpointStatus(
            attribution.isLoading,
            attribution.error,
            !!(attribution.data && attribution.data.length > 0)
        ),
        commissions: getEndpointStatus(
            commissions.isLoading,
            commissions.error,
            !!(commissions.data && commissions.data.length > 0)
        ),
        payouts: getEndpointStatus(
            payouts.isLoading,
            payouts.error,
            !!(payouts.data && payouts.data.rows.length > 0)
        ),
        fraud: getEndpointStatus(
            fraud.isLoading,
            fraud.error,
            !!(fraud.data && fraud.data.length > 0)
        )
    };

    const firstContractIssue = [
        kpis.error,
        applications.error,
        partners.error,
        partnerDetail.error,
        attribution.error,
        commissions.error,
        payouts.error,
        fraud.error
    ].find((error) => error);
    const isMissingContract =
        contractStatus.kpis === 'missing' ||
        contractStatus.applications === 'missing' ||
        contractStatus.partners === 'missing' ||
        contractStatus.detail === 'missing' ||
        contractStatus.attribution === 'missing' ||
        contractStatus.commissions === 'missing' ||
        contractStatus.payouts === 'missing' ||
        contractStatus.fraud === 'missing';
    const isContractReady =
        !isMissingContract &&
        !firstContractIssue &&
        (contractStatus.kpis === 'ready' || contractStatus.kpis === 'empty') &&
        (contractStatus.applications === 'ready' || contractStatus.applications === 'empty') &&
        (contractStatus.partners === 'ready' || contractStatus.partners === 'empty') &&
        (contractStatus.detail === 'ready' || contractStatus.detail === 'empty') &&
        (contractStatus.attribution === 'ready' || contractStatus.attribution === 'empty') &&
        (contractStatus.commissions === 'ready' || contractStatus.commissions === 'empty') &&
        (contractStatus.payouts === 'ready' || contractStatus.payouts === 'empty') &&
        (contractStatus.fraud === 'ready' || contractStatus.fraud === 'empty');

    const kpiRows: Kpi[] = [
        {
            id: 'active-affiliates',
            label: 'Active affiliates',
            value: kpis.data ? `${kpis.data.active_affiliates.toLocaleString()}` : '—',
            delta: { direction: 'flat', pct: '±0%' },
            spark: FLAT_SPARK,
            href: '/affiliate-manager'
        },
        {
            id: 'applications',
            label: 'Applications pending',
            value: kpis.data ? `${kpis.data.applications_pending.toLocaleString()}` : '—',
            delta: { direction: 'flat', pct: '±0' },
            spark: FLAT_SPARK,
            href: '/affiliate-manager',
            alert: contractStatus.applications === 'empty'
        },
        {
            id: 'attributed-trials',
            label: 'Attributed trials (30d)',
            value: kpis.data ? `${kpis.data.attributed_trials_30d.toLocaleString()}` : '—',
            delta: { direction: 'flat', pct: '±0%' },
            spark: FLAT_SPARK,
            href: '/affiliate-manager'
        },
        {
            id: 'attributed-paid',
            label: 'Paid conversions (30d)',
            value: kpis.data ? `${kpis.data.paid_conversions_30d.toLocaleString()}` : '—',
            delta: { direction: 'flat', pct: '±0%' },
            spark: FLAT_SPARK,
            href: '/affiliate-manager'
        },
        {
            id: 'commission-due',
            label: 'Commission due',
            value: kpis.data ? formatMoney(kpis.data.commission_due) : '—',
            delta: { direction: 'flat', pct: '±0%' },
            spark: FLAT_SPARK,
            href: '/affiliate-manager'
        },
        {
            id: 'fraud-queue',
            label: 'Fraud queue',
            value: kpis.data ? `${kpis.data.fraud_queue.toLocaleString()}` : '—',
            delta: { direction: 'flat', pct: '±0' },
            spark: FLAT_SPARK,
            href: '/affiliate-manager',
            alert: !!kpis.data?.fraud_queue
        }
    ];

    const refreshAll = async () =>
        Promise.all([
            queryClient.invalidateQueries({ queryKey: ['admin', 'affiliate-manager', 'kpis'] }),
            queryClient.invalidateQueries({
                queryKey: ['admin', 'affiliate-manager', 'applications']
            }),
            queryClient.invalidateQueries({ queryKey: ['admin', 'affiliate-manager', 'partners'] }),
            queryClient.invalidateQueries({ queryKey: ['admin', 'affiliate-manager', 'partner'] }),
            queryClient.invalidateQueries({
                queryKey: ['admin', 'affiliate-manager', 'attribution']
            }),
            queryClient.invalidateQueries({
                queryKey: ['admin', 'affiliate-manager', 'commissions']
            }),
            queryClient.invalidateQueries({ queryKey: ['admin', 'affiliate-manager', 'payouts'] }),
            queryClient.invalidateQueries({ queryKey: ['admin', 'affiliate-manager', 'fraud'] })
        ]);

    const handleExport = async () => {
        setExportBusy(true);
        try {
            const csv = await apiDownloadText(buildStatementExportUrl(period), 'text/csv');
            const href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
            const anchor = document.createElement('a');
            anchor.href = href;
            anchor.download = `affiliate-statement-${period}-${new Date().toISOString().slice(0, 10)}.csv`;
            anchor.click();
            URL.revokeObjectURL(href);
            toast('Monthly statement exported', { detail: toPeriodLabel(period) });
        } catch (error) {
            toast('Statement export failed', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Export did not complete')
            });
        } finally {
            setExportBusy(false);
        }
    };

    const promptReason = (message: string) => {
        const reason = window.prompt(message);
        return reason?.trim() || '';
    };

    const holdPayout = async (payoutId: string, label: string) => {
        const reason = promptReason(`Reason to hold payout ${label}`);
        if (!reason) return;
        try {
            await apiPost(actionToPayoutEndpoint('hold', payoutId), { reason });
            await refreshAll();
            toast('Payout held', { detail: label });
        } catch (error) {
            toast('Failed to hold payout', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Hold request failed')
            });
        }
    };

    const releasePayout = async (payoutId: string, label: string) => {
        const reason = promptReason(`Reason to release payout ${label}`);
        if (!reason) return;
        try {
            const result = await apiPost<{ success: boolean; provider_unavailable?: boolean }>(
                actionToPayoutEndpoint('release', payoutId),
                { reason }
            );
            await refreshAll();
            toast(
                result.provider_unavailable
                    ? 'Payout marked ready for manual transfer'
                    : 'Payout released',
                {
                    detail: label
                }
            );
        } catch (error) {
            toast('Failed to release payout', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Release request failed')
            });
        }
    };

    const resolveFraud = async (
        flagId: string,
        label: string,
        resolution: 'resolve' | 'dismiss' | 'block_payout'
    ) => {
        const reason = promptReason(
            `Reason to ${resolution.replace('_', ' ')} fraud flag ${label}`
        );
        if (!reason) return;
        try {
            await apiPost(actionToFraudEndpoint(flagId), { reason, resolution });
            await refreshAll();
            toast('Fraud flag updated', { detail: label });
        } catch (error) {
            toast('Failed to update fraud flag', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Fraud resolution failed')
            });
        }
    };

    const createPartnerLink = async () => {
        if (!detailPartnerId) return;
        try {
            await apiPost(`/api/admin/affiliates/partners/${detailPartnerId}/links`, {});
            await refreshAll();
            toast('Tracked link created', {
                detail: partnerDetail.data?.affiliate.handle || detailPartnerId
            });
        } catch (error) {
            toast('Failed to create tracked link', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Tracked link creation failed')
            });
        }
    };

    const updatePartner = async (patch: { status?: string; commission_rate?: number }) => {
        if (!detailPartnerId) return;
        const reason = promptReason('Reason for partner update');
        if (!reason) return;
        try {
            await apiPatch(`/api/admin/affiliates/partners/${detailPartnerId}`, {
                ...patch,
                reason
            });
            await refreshAll();
            toast('Partner updated', {
                detail: partnerDetail.data?.affiliate.handle || detailPartnerId
            });
        } catch (error) {
            toast('Failed to update partner', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Partner update failed')
            });
        }
    };

    const adjustPartnerRate = async () => {
        if (!detailPartnerId) return;
        const rateInput = window.prompt('New commission rate (%)');
        if (!rateInput?.trim()) return;
        const nextRate = Number(rateInput);
        if (!Number.isFinite(nextRate) || nextRate < 0 || nextRate > 100) {
            toast('Invalid rate', {
                kind: 'danger',
                detail: 'Enter a percentage between 0 and 100.'
            });
            return;
        }
        await updatePartner({ commission_rate: nextRate });
    };

    const closeReviewAction = () => {
        setReviewAction(null);
        setReviewReason('');
    };

    const submitReviewAction = async () => {
        if (!reviewAction) return;
        const requiresReason = reviewAction.action === 'reject';
        if (requiresReason && !reviewReason.trim()) {
            toast('Reason required', {
                kind: 'danger',
                detail: 'Reject actions require a reason so admin audit can be attributed.'
            });
            return;
        }

        setReviewBusy(true);
        const endpoint =
            reviewAction.action === 'approve'
                ? `/api/admin/affiliates/applications/${reviewAction.applicationId}/approve`
                : `/api/admin/affiliates/applications/${reviewAction.applicationId}/reject`;
        const verb = reviewAction.action === 'approve' ? 'approved' : 'rejected';

        try {
            await apiPost<{ success: true }>(endpoint, {
                reason: reviewReason.trim() || undefined
            });
            await refreshAll();
            toast(`Application ${verb}`, {
                detail: `${reviewAction.applicantLabel} · ${reviewAction.action}`
            });
            closeReviewAction();
        } catch (error) {
            toast(`Failed to ${reviewAction.action} application`, {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Action was not persisted')
            });
        } finally {
            setReviewBusy(false);
        }
    };

    return (
        <div className="stack-16">
            <header className="page-header">
                <div className="page-title-group">
                    <h1>Affiliate manager</h1>
                    <p>Applications, partner roster, and conversion pipeline health.</p>
                </div>
                <div className="page-actions">
                    <select
                        className="select"
                        value={period}
                        aria-label="Statement period"
                        onChange={(event) => setPeriod(event.target.value as AffiliatePeriod)}
                    >
                        <option value="30d">Last 30 days</option>
                        <option value="90d">Last 90 days</option>
                        <option value="ytd">Year to date</option>
                    </select>
                    <button
                        className="btn btn-ghost"
                        type="button"
                        disabled
                        title="No endpoint yet: /api/admin/affiliates/applications/:id/duplicate"
                    >
                        New partner invite
                    </button>
                    <RoleButton
                        scope="support_ok"
                        className="btn btn-ghost"
                        disabled={exportBusy}
                        onClick={handleExport}
                    >
                        <Download size={14} /> {exportBusy ? 'Exporting…' : 'Export statement'}
                    </RoleButton>
                </div>
            </header>

            <section className="alert-bar">
                <div
                    className="alert"
                    data-severity={
                        isMissingContract ? 'danger' : firstContractIssue ? 'danger' : 'info'
                    }
                >
                    {isMissingContract ? (
                        <>
                            <Megaphone size={14} />
                            <span className="msg">
                                Affiliate backend route(s) are not yet mounted in the main Worker.
                            </span>
                            <span className="meta">
                                The page is wired and ready to consume real records at the contract
                                defined below.
                            </span>
                        </>
                    ) : firstContractIssue ? (
                        <>
                            <ShieldAlert size={14} />
                            <span className="msg">
                                One or more affiliate endpoints are returning errors. Live rows are
                                shown only for successful responses.
                            </span>
                            <span className="meta">
                                {getHttpErrorMessage(
                                    firstContractIssue,
                                    'Affiliate endpoint error'
                                )}
                            </span>
                        </>
                    ) : (
                        <>
                            <ShieldAlert size={14} />
                            <span className="msg">
                                {contractStatus.kpis === 'ready' ||
                                contractStatus.kpis === 'empty' ||
                                contractStatus.applications === 'ready' ||
                                contractStatus.applications === 'empty' ||
                                contractStatus.partners === 'ready' ||
                                contractStatus.partners === 'empty'
                                    ? 'Affiliate data sources are loaded from the admin API.'
                                    : 'Loading affiliate contract sources…'}
                            </span>
                        </>
                    )}
                </div>
            </section>

            <section className="kpi-grid kpi-grid-6" aria-label="Affiliate program metrics">
                {kpiRows.map((kpi) => (
                    <KpiTile key={kpi.id} kpi={kpi} />
                ))}
            </section>

            <div className="grid-split">
                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Applications queue</h2>
                        <Chip tone="muted">
                            {contractStatus.applications === 'ready'
                                ? `${applications.data?.length ?? 0} rows`
                                : endpointStatusLabel(contractStatus.applications)}
                        </Chip>
                    </header>
                    <div className="table-wrap">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Applicant</th>
                                    <th>Channels</th>
                                    <th>Audience</th>
                                    <th>Region</th>
                                    <th>Submitted</th>
                                    <th>Risk</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {applications.isLoading ? (
                                    <tr>
                                        <td colSpan={7} className="text-muted">
                                            Loading applications…
                                        </td>
                                    </tr>
                                ) : applications.data?.length ? (
                                    applications.data.map((app) => (
                                        <tr key={app.id}>
                                            <td>
                                                <div style={{ display: 'grid', gap: 3 }}>
                                                    <span
                                                        className="text-muted"
                                                        style={{ color: 'var(--text-primary)' }}
                                                    >
                                                        {app.applicant_name}
                                                    </span>
                                                    <span
                                                        className="text-muted"
                                                        style={{ fontSize: 11 }}
                                                    >
                                                        {app.applicant_email}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                {app.channels.length
                                                    ? app.channels.join(', ')
                                                    : '—'}
                                            </td>
                                            <td className="numeric">
                                                {formatInteger(app.audience_size)}
                                            </td>
                                            <td>{app.geo}</td>
                                            <td>{formatDate(app.submitted_at)}</td>
                                            <td>{app.risk_hints.join(', ') || '—'}</td>
                                            <td
                                                style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                                            >
                                                <RoleButton
                                                    scope="owner_only"
                                                    className="btn btn-sm btn-ghost"
                                                    onClick={() =>
                                                        setReviewAction({
                                                            applicationId: app.id,
                                                            applicantLabel: app.applicant_name,
                                                            action: 'approve'
                                                        })
                                                    }
                                                    disabled={
                                                        contractStatus.applications !== 'ready' &&
                                                        contractStatus.applications !== 'empty'
                                                    }
                                                >
                                                    <CheckCircle2 size={14} /> Approve
                                                </RoleButton>
                                                <RoleButton
                                                    scope="owner_only"
                                                    className="btn btn-sm btn-ghost"
                                                    onClick={() =>
                                                        setReviewAction({
                                                            applicationId: app.id,
                                                            applicantLabel: app.applicant_name,
                                                            action: 'reject'
                                                        })
                                                    }
                                                    disabled={
                                                        contractStatus.applications !== 'ready' &&
                                                        contractStatus.applications !== 'empty'
                                                    }
                                                >
                                                    <XCircle size={14} /> Reject
                                                </RoleButton>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={7}>
                                            <div className="empty-state">
                                                <div className="title">
                                                    No applications in queue
                                                </div>
                                                <div className="sub">
                                                    Either there are no pending applicants, or
                                                    <code>
                                                        /api/admin/affiliates/applications
                                                    </code>{' '}
                                                    is not returning records yet.
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <footer className="card-footer">
                        <span className="text-muted">
                            Action contract note: request-info and duplicate are intentionally
                            disabled until dedicated endpoints are added.
                        </span>
                    </footer>
                </section>

                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Partner roster</h2>
                        <Chip tone="info">
                            {contractStatus.partners === 'ready'
                                ? `${partners.data?.length ?? 0} partners`
                                : endpointStatusLabel(contractStatus.partners)}
                        </Chip>
                    </header>
                    <div className="table-wrap">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Partner</th>
                                    <th>Handle</th>
                                    <th>Status</th>
                                    <th>Primary code</th>
                                    <th className="numeric">Clicks</th>
                                    <th className="numeric">Signups</th>
                                    <th className="numeric">Paid</th>
                                    <th className="numeric">Conversion</th>
                                    <th className="numeric">Revenue</th>
                                    <th className="numeric">Commission due</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {partners.isLoading ? (
                                    <tr>
                                        <td colSpan={11} className="text-muted">
                                            Loading roster…
                                        </td>
                                    </tr>
                                ) : partners.data?.length ? (
                                    partners.data.map((p) => (
                                        <tr key={p.id}>
                                            <td>{p.name}</td>
                                            <td>{p.handle}</td>
                                            <td>
                                                <Chip tone={statusTone(p.status)}>{p.status}</Chip>
                                            </td>
                                            <td>{p.primary_code || '—'}</td>
                                            <td className="numeric">{formatInteger(p.clicks)}</td>
                                            <td className="numeric">{formatInteger(p.signups)}</td>
                                            <td className="numeric">
                                                {formatInteger(p.paid_conversions)}
                                            </td>
                                            <td className="numeric">
                                                {formatRate(p.conversion_rate)}
                                            </td>
                                            <td className="numeric">
                                                {formatMoney(p.lifetime_revenue)}
                                            </td>
                                            <td className="numeric">
                                                {formatMoney(p.commission_due)}
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                <button
                                                    className="btn btn-sm btn-ghost"
                                                    type="button"
                                                    onClick={() => setDetailPartnerId(p.id)}
                                                >
                                                    <Eye size={14} /> Inspect
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={11}>
                                            <div className="empty-state">
                                                <div className="title">No partner rows yet</div>
                                                <div className="sub">
                                                    <code>/api/admin/affiliates/partners</code> is
                                                    empty or not yet wired.
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            <div className="grid-split">
                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Attribution inspector</h2>
                        <Chip tone="info">
                            {attribution.data?.length ?? (attribution.isLoading ? '…' : 0)} rows
                        </Chip>
                    </header>
                    <div className="card-body tight">
                        {attribution.isLoading ? (
                            <div className="empty-state">Loading attribution rows…</div>
                        ) : attribution.data?.length ? (
                            attribution.data.map((row) => (
                                <div
                                    key={row.id}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1.2fr auto auto auto',
                                        gap: 10,
                                        padding: '10px 16px',
                                        borderBottom: '1px solid var(--border)',
                                        fontSize: 12
                                    }}
                                >
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ color: 'var(--text-primary)' }}>
                                            {row.user_name || 'Unknown user'}
                                        </div>
                                        <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                                            {row.user_email || 'no email'} ·{' '}
                                            {row.link_code || 'no link'}
                                        </div>
                                        <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                                            {row.detail}
                                        </div>
                                        {row.chain.length > 0 && (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 6,
                                                    marginTop: 6
                                                }}
                                            >
                                                {row.chain.map((step) => (
                                                    <Chip key={step.id} tone="muted">
                                                        {step.event_type}
                                                    </Chip>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <Chip
                                        tone={
                                            row.event_type === 'paid_conversion'
                                                ? 'success'
                                                : 'info'
                                        }
                                    >
                                        {row.event_type.replace(/_/g, ' ')}
                                    </Chip>
                                    <span className="numeric mono">{formatMoney(row.amount)}</span>
                                    <span className="text-muted">
                                        {formatShortDateTime(row.occurred_at)}
                                    </span>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                <div className="title">
                                    No attribution rows in the current window
                                </div>
                                <div className="sub">
                                    The live program data has not recorded click / signup /
                                    conversion rows for {toPeriodLabel(period).toLowerCase()}.
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Commission ledger</h2>
                        <Chip tone="purple">
                            {commissions.data?.length ?? (commissions.isLoading ? '…' : 0)} rows
                        </Chip>
                    </header>
                    <div className="card-body tight">
                        {commissions.isLoading ? (
                            <div className="empty-state">Loading commission rows…</div>
                        ) : commissions.data?.length ? (
                            commissions.data.map((row) => (
                                <div
                                    key={row.id}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr auto auto auto',
                                        gap: 10,
                                        padding: '10px 16px',
                                        borderBottom: '1px solid var(--border)',
                                        fontSize: 12
                                    }}
                                >
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ color: 'var(--text-primary)' }}>
                                            {row.partner}
                                        </div>
                                        <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                                            {row.handle} · {row.period}
                                        </div>
                                        {row.event_chain.length > 0 && (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 6,
                                                    marginTop: 6
                                                }}
                                            >
                                                {row.event_chain.map((step) => (
                                                    <Chip key={step.id} tone="muted">
                                                        {step.event_type}
                                                    </Chip>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <Chip
                                        tone={
                                            row.status === 'paid'
                                                ? 'success'
                                                : row.status === 'held'
                                                  ? 'warning'
                                                  : 'info'
                                        }
                                    >
                                        {row.status}
                                    </Chip>
                                    <span className="mono">{formatMoney(row.final_payable)}</span>
                                    <span className="text-muted">
                                        {formatShortDate(row.due_at || row.paid_at)}
                                    </span>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                <div className="title">
                                    No commission rows in the current window
                                </div>
                                <div className="sub">
                                    Commission accruals will appear once live attribution rows
                                    exist.
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            <div className="grid-split">
                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Payout queue</h2>
                        <Chip tone={payouts.data?.provider_available ? 'success' : 'warning'}>
                            {payouts.data?.provider_available
                                ? 'Provider available'
                                : 'Manual fallback'}
                        </Chip>
                    </header>
                    <div className="card-body tight">
                        {payouts.isLoading ? (
                            <div className="empty-state">Loading payouts…</div>
                        ) : payouts.data?.rows.length ? (
                            payouts.data.rows.map((row) => (
                                <div
                                    key={row.id}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr auto auto auto auto',
                                        gap: 10,
                                        padding: '10px 16px',
                                        borderBottom: '1px solid var(--border)',
                                        fontSize: 12
                                    }}
                                >
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ color: 'var(--text-primary)' }}>
                                            {row.partner}
                                        </div>
                                        <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                                            {row.handle} · {row.provider}
                                            {row.provider_unavailable ? ' · manual only' : ''}
                                        </div>
                                        {row.held_reason && (
                                            <div
                                                style={{ color: 'var(--text-muted)', fontSize: 11 }}
                                            >
                                                Held: {row.held_reason}
                                            </div>
                                        )}
                                    </div>
                                    <Chip
                                        tone={
                                            row.status === 'held'
                                                ? 'warning'
                                                : row.status === 'paid'
                                                  ? 'success'
                                                  : 'info'
                                        }
                                    >
                                        {row.status}
                                    </Chip>
                                    <span className="mono">{formatMoney(row.amount)}</span>
                                    <span className="text-muted">
                                        {formatShortDateTime(row.created_at)}
                                    </span>
                                    <div
                                        style={{
                                            display: 'flex',
                                            gap: 8,
                                            justifyContent: 'flex-end'
                                        }}
                                    >
                                        <RoleButton
                                            scope="owner_only"
                                            className="btn btn-sm btn-ghost"
                                            onClick={() =>
                                                holdPayout(
                                                    row.id,
                                                    `${row.partner} · ${row.amount.toFixed(2)}`
                                                )
                                            }
                                        >
                                            Hold
                                        </RoleButton>
                                        <RoleButton
                                            scope="owner_only"
                                            className="btn btn-sm btn-ghost"
                                            onClick={() =>
                                                releasePayout(
                                                    row.id,
                                                    `${row.partner} · ${row.amount.toFixed(2)}`
                                                )
                                            }
                                        >
                                            Release
                                        </RoleButton>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                <div className="title">No payout rows yet</div>
                                <div className="sub">
                                    Payouts are currently manual-first; release only advances the
                                    row to an approved state and never fakes a provider transfer.
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Fraud review</h2>
                        <Chip tone={fraud.data?.length ? 'warning' : 'success'}>
                            {fraud.data?.length ?? 0} open
                        </Chip>
                    </header>
                    <div className="card-body tight">
                        {fraud.isLoading ? (
                            <div className="empty-state">Loading fraud rows…</div>
                        ) : fraud.data?.length ? (
                            fraud.data.map((row) => (
                                <div
                                    key={row.id}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr auto auto auto',
                                        gap: 10,
                                        padding: '10px 16px',
                                        borderBottom: '1px solid var(--border)',
                                        fontSize: 12
                                    }}
                                >
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ color: 'var(--text-primary)' }}>
                                            {row.partner}
                                        </div>
                                        <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                                            {row.handle} · {row.reason}
                                        </div>
                                        {row.payout_status && (
                                            <div
                                                style={{ color: 'var(--text-muted)', fontSize: 11 }}
                                            >
                                                Payout: {row.payout_status}
                                            </div>
                                        )}
                                    </div>
                                    <Chip
                                        tone={
                                            row.severity === 'high'
                                                ? 'danger'
                                                : row.severity === 'medium'
                                                  ? 'warning'
                                                  : 'info'
                                        }
                                    >
                                        {row.severity}
                                    </Chip>
                                    <Chip tone={row.status === 'resolved' ? 'success' : 'warning'}>
                                        {row.status}
                                    </Chip>
                                    <div
                                        style={{
                                            display: 'flex',
                                            gap: 8,
                                            justifyContent: 'flex-end'
                                        }}
                                    >
                                        <RoleButton
                                            scope="owner_only"
                                            className="btn btn-sm btn-ghost"
                                            onClick={() =>
                                                resolveFraud(row.id, row.partner, 'resolve')
                                            }
                                        >
                                            Resolve
                                        </RoleButton>
                                        <RoleButton
                                            scope="owner_only"
                                            className="btn btn-sm btn-ghost"
                                            onClick={() =>
                                                resolveFraud(row.id, row.partner, 'dismiss')
                                            }
                                        >
                                            Dismiss
                                        </RoleButton>
                                        <RoleButton
                                            scope="owner_only"
                                            className="btn btn-sm btn-danger"
                                            onClick={() =>
                                                resolveFraud(row.id, row.partner, 'block_payout')
                                            }
                                        >
                                            Block payout
                                        </RoleButton>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                <div className="title">No fraud flags in review</div>
                                <div className="sub">
                                    Open or reviewing flags will appear here and can hold payouts
                                    before release.
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Affiliate API contract</h2>
                    <Chip tone={isContractReady ? 'success' : 'warning'}>
                        {isContractReady ? 'Worker-backed' : 'Wiring required in main Worker'}
                    </Chip>
                </header>
                <div className="card-body tight">
                    {CONTRACT_ROUTES.map((route) => {
                        let status: EndpointStatus = 'missing';
                        if (route.endpoint === '/api/admin/affiliates/kpis')
                            status = contractStatus.kpis;
                        else if (route.endpoint === '/api/admin/affiliates/applications')
                            status = contractStatus.applications;
                        else if (
                            route.endpoint === '/api/admin/affiliates/applications/:id/approve' ||
                            route.endpoint === '/api/admin/affiliates/applications/:id/reject'
                        )
                            status =
                                contractStatus.applications === 'ready' ||
                                contractStatus.applications === 'empty'
                                    ? 'ready'
                                    : contractStatus.applications;
                        else if (route.endpoint === '/api/admin/affiliates/partners')
                            status = contractStatus.partners;
                        else if (route.endpoint === '/api/admin/affiliates/partners/:id')
                            status = contractStatus.detail;
                        else if (
                            route.endpoint === '/api/admin/affiliates/partners/:id/links' ||
                            route.endpoint === '/api/admin/affiliates/attribution' ||
                            route.endpoint === '/api/admin/affiliates/commissions'
                        )
                            status =
                                contractStatus.partners === 'ready' ||
                                contractStatus.partners === 'empty'
                                    ? 'ready'
                                    : contractStatus.partners;
                        else if (route.endpoint === '/api/admin/affiliates/payouts')
                            status = contractStatus.payouts;
                        else if (
                            route.endpoint === '/api/admin/affiliates/payouts/:id/hold' ||
                            route.endpoint === '/api/admin/affiliates/payouts/:id/release' ||
                            route.endpoint === '/api/admin/affiliates/fraud/:id/resolve'
                        )
                            status =
                                contractStatus.payouts === 'ready' ||
                                contractStatus.payouts === 'empty'
                                    ? 'ready'
                                    : contractStatus.payouts;
                        else if (route.endpoint === '/api/admin/affiliates/fraud')
                            status = contractStatus.fraud;
                        else if (route.endpoint === '/api/admin/affiliates/export.csv')
                            status =
                                contractStatus.commissions === 'ready' ||
                                contractStatus.commissions === 'empty'
                                    ? 'ready'
                                    : contractStatus.commissions;
                        else status = 'missing';

                        return (
                            <div
                                key={route.endpoint}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'auto 1fr auto',
                                    gap: 10,
                                    alignItems: 'center',
                                    borderBottom: '1px solid var(--border)',
                                    padding: '10px 16px',
                                    fontSize: 12
                                }}
                            >
                                <span style={{ minWidth: 56, color: 'var(--text-muted)' }}>
                                    {route.method}
                                </span>
                                <code
                                    style={{ fontSize: 12 }}
                                >{`${route.method} ${route.endpoint}`}</code>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <Chip tone={getContractChipTone(status)}>
                                        {endpointStatusLabel(status)}
                                    </Chip>
                                    <span className="text-muted">{route.purpose}</span>
                                </div>
                            </div>
                        );
                    })}
                    <div style={{ padding: '10px 16px' }}>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                            {isContractReady ? (
                                <>
                                    Affiliate manager is now backed by main Worker routes. Empty
                                    rows mean there are no affiliate records yet.
                                </>
                            ) : (
                                <>
                                    If these rows read <strong>Missing route</strong>, create
                                    <code> src/admin/affiliates.js</code> in the main Worker and
                                    wire the routes in <code> src/apirouter.js</code>.
                                </>
                            )}
                        </span>
                    </div>
                </div>
            </section>

            <Modal
                open={Boolean(detailPartnerId)}
                title={partnerDetail.data?.affiliate?.name || 'Partner detail'}
                subtitle={
                    partnerDetail.data
                        ? `${partnerDetail.data.affiliate.handle} · ${partnerDetail.data.affiliate.status}`
                        : detailPartnerId || 'Loading partner detail'
                }
                onClose={() => setDetailPartnerId(null)}
                width={960}
            >
                {partnerDetail.isLoading ? (
                    <div className="empty-state">
                        <div className="title">Loading partner detail…</div>
                        <div className="sub">
                            Fetching attribution chain, commissions, payouts, and fraud flags.
                        </div>
                    </div>
                ) : partnerDetail.error ? (
                    <div className="empty-state">
                        <div className="title">
                            {isContractMissing(partnerDetail.error)
                                ? 'Partner detail route missing'
                                : 'Failed to load partner detail'}
                        </div>
                        <div className="sub">
                            {getHttpErrorMessage(
                                partnerDetail.error,
                                'The worker could not return partner detail'
                            )}
                        </div>
                    </div>
                ) : !partnerDetail.data ? (
                    <div className="empty-state">
                        <div className="title">Partner detail unavailable</div>
                        <div className="sub">No partner row matched the selected id.</div>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gap: 14 }}>
                        <section className="detail-panel-section">
                            <header
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 12
                                }}
                            >
                                <h3>Partner summary</h3>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <Chip tone={statusTone(partnerDetail.data.affiliate.status)}>
                                        {partnerDetail.data.affiliate.status}
                                    </Chip>
                                    <Chip tone="info">
                                        {partnerDetail.data.affiliate.primary_code || 'no code'}
                                    </Chip>
                                </div>
                            </header>
                            <dl className="kv-list">
                                <dt>Commission rate</dt>
                                <dd>
                                    {formatDecimal(partnerDetail.data.affiliate.commission_rate)}%
                                </dd>
                                <dt>Active links</dt>
                                <dd>{partnerDetail.data.affiliate.active_links}</dd>
                                <dt>Commission due</dt>
                                <dd>{formatMoney(partnerDetail.data.affiliate.commission_due)}</dd>
                                <dt>Commission paid</dt>
                                <dd>{formatMoney(partnerDetail.data.affiliate.commission_paid)}</dd>
                                <dt>Lifetime revenue</dt>
                                <dd>
                                    {formatMoney(partnerDetail.data.affiliate.lifetime_revenue)}
                                </dd>
                            </dl>
                            <div
                                style={{ marginTop: 10, color: 'var(--text-muted)', fontSize: 12 }}
                            >
                                {partnerDetail.data.affiliate.notes || 'No partner notes recorded.'}
                            </div>
                            <div
                                style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}
                            >
                                <RoleButton
                                    scope="owner_only"
                                    className="btn btn-sm btn-ghost"
                                    onClick={() =>
                                        updatePartner({
                                            status:
                                                partnerDetail.data.affiliate.status === 'paused'
                                                    ? 'active'
                                                    : 'paused'
                                        })
                                    }
                                >
                                    {partnerDetail.data.affiliate.status === 'paused'
                                        ? 'Resume partner'
                                        : 'Pause partner'}
                                </RoleButton>
                                <RoleButton
                                    scope="owner_only"
                                    className="btn btn-sm btn-ghost"
                                    onClick={adjustPartnerRate}
                                >
                                    Adjust rate
                                </RoleButton>
                                <RoleButton
                                    scope="owner_only"
                                    className="btn btn-sm btn-ghost"
                                    onClick={createPartnerLink}
                                >
                                    Rotate link
                                </RoleButton>
                            </div>
                        </section>

                        <section className="detail-panel-section">
                            <header
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 12
                                }}
                            >
                                <h3>Active links</h3>
                                <Chip tone="muted">
                                    {
                                        partnerDetail.data.links.filter((link) => link.is_active)
                                            .length
                                    }{' '}
                                    active
                                </Chip>
                            </header>
                            <div>
                                {partnerDetail.data.links.length ? (
                                    partnerDetail.data.links.map((link) => (
                                        <div
                                            key={link.id}
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr auto',
                                                gap: 12,
                                                padding: '8px 16px',
                                                borderBottom: '1px solid var(--border)',
                                                fontSize: 12
                                            }}
                                        >
                                            <div>
                                                <div style={{ color: 'var(--text-primary)' }}>
                                                    {link.code}
                                                </div>
                                                <div
                                                    style={{
                                                        color: 'var(--text-muted)',
                                                        fontSize: 11
                                                    }}
                                                >
                                                    {link.destination_url}
                                                </div>
                                            </div>
                                            <Chip tone={link.is_active ? 'success' : 'muted'}>
                                                {link.is_active ? 'active' : 'inactive'}
                                            </Chip>
                                        </div>
                                    ))
                                ) : (
                                    <div className="empty-state">No links yet.</div>
                                )}
                            </div>
                        </section>

                        <section className="detail-panel-section">
                            <header
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 12
                                }}
                            >
                                <h3>Event chains</h3>
                                <Chip
                                    tone={
                                        partnerDetail.data.event_chains.length ? 'warning' : 'muted'
                                    }
                                >
                                    {partnerDetail.data.event_chains.length} payable conversions
                                </Chip>
                            </header>
                            <div>
                                {partnerDetail.data.event_chains.length ? (
                                    partnerDetail.data.event_chains.map((chain) => (
                                        <div
                                            key={chain.id}
                                            style={{
                                                padding: '10px 16px',
                                                borderBottom: '1px solid var(--border)',
                                                fontSize: 12
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    flexWrap: 'wrap'
                                                }}
                                            >
                                                <Chip tone="success">
                                                    {formatMoney(chain.amount)}
                                                </Chip>
                                                <span style={{ color: 'var(--text-primary)' }}>
                                                    {chain.user_email || 'Unknown user'}
                                                </span>
                                                <span className="text-muted">
                                                    {formatShortDateTime(chain.occurred_at)}
                                                </span>
                                            </div>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 6,
                                                    marginTop: 8
                                                }}
                                            >
                                                {chain.chain.length ? (
                                                    chain.chain.map((step) => (
                                                        <Chip key={step.id} tone="muted">
                                                            {step.event_type}
                                                        </Chip>
                                                    ))
                                                ) : (
                                                    <span className="text-muted">
                                                        No chain rows available
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="empty-state">
                                        <div className="title">No payable conversions yet</div>
                                        <div className="sub">
                                            The worker has not seen enough attribution events to
                                            build a payable chain for this partner.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>
                )}
            </Modal>

            <Modal
                open={Boolean(reviewAction)}
                title={
                    reviewAction?.action === 'approve'
                        ? 'Approve application'
                        : 'Reject application'
                }
                subtitle={
                    reviewAction
                        ? `${reviewAction.applicantLabel} · ${reviewAction.action}`
                        : 'Affiliate application review'
                }
                onClose={closeReviewAction}
                width={580}
                footer={
                    <>
                        <button
                            className="btn btn-ghost"
                            onClick={closeReviewAction}
                            disabled={reviewBusy}
                        >
                            Cancel
                        </button>
                        <RoleButton
                            scope="owner_only"
                            className="btn btn-primary"
                            onClick={submitReviewAction}
                            disabled={
                                reviewBusy ||
                                (reviewAction?.action === 'reject' && !reviewReason.trim())
                            }
                        >
                            {reviewBusy ? 'Submitting…' : 'Submit decision'}
                        </RoleButton>
                    </>
                }
            >
                <div style={{ display: 'grid', gap: 10 }}>
                    <div style={{ display: 'grid', gap: 4 }}>
                        <span className="form-label">Reason</span>
                        <textarea
                            value={reviewReason}
                            onChange={(event) => setReviewReason(event.target.value)}
                            placeholder={
                                reviewAction?.action === 'approve'
                                    ? 'Optional context (for audit trail, if any).'
                                    : 'Required: reason is required for reject to satisfy audit policy.'
                            }
                            rows={5}
                            className="form-textarea"
                        />
                    </div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                        All review writes are server-authoritative. If this action fails, row state
                        is unchanged.
                    </div>
                </div>
            </Modal>
        </div>
    );
}
